import {
	CloseSupportCaseData,
	CloseSupportCaseResponse,
	EscalateSupportCaseData,
	EscalateSupportCaseRequest,
	EscalateSupportCaseResponse,
	GetSupportCasesData,
	GetSupportCasesResponse,
	GetSupportContactsData,
	GetSupportContactsResponse,
	GetSupportOrdersArgs,
	GetSupportOrdersData,
	GetSupportOrdersResponse,
	CreateSupportCaseData,
	CreateSupportCaseResponse,
	SupportCaseSubmission
} from "./supportTypes.ts";
import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithRedirect } from "../../api/utils/apiUtils.ts";
import endpoints, { baseURL } from "../../../data/api/v1/endpoints.ts";
import { isDevEnv } from "constants/environment";

type GetSupportCasesArgs = {
	search?: string;
};

type EscalateSupportCaseArgs = {
	caseId: string;
	request: EscalateSupportCaseRequest;
};

type CloseSupportCaseArgs = {
	caseId: string;
};

export const supportApiSlice = createApi({
	reducerPath: "supportApi",
	baseQuery: baseQueryWithRedirect(baseURL),
	tagTypes: ["SupportCases"],
	endpoints: (builder) => ({
		createSupportCase: builder.mutation<CreateSupportCaseData, SupportCaseSubmission>({
			async queryFn(request, api, extraOptions) {
				const body = new FormData();
				const isMocked = isDevEnv && import.meta.env.VITE_APP_MOCKED;
				const {
					attachments,
					caseType,
					accountId: _accountId,
					accountNumber,
					orderId: _orderId,
					problemType,
					name,
					url: webpage,
					...fields
				} = request;
				const apiFields = {
					...fields,
					type: caseType,
					typeOfProblem: problemType,
					customerName: name,
					webpage,
					accountNumber,
					notes: request.description
				};
				Object.entries(isMocked ? { ...fields, problemType, name, url: webpage } : apiFields).forEach(
					([key, value]) => {
						if (value !== undefined && value !== "") body.append(key, value);
					}
				);
				attachments.forEach((file) => body.append(isMocked ? "attachments" : "files", file));
				const targetUrl = isMocked ? `${baseURL}/__mocks__/support/cases` : endpoints.support.createCase();
				const result = await baseQueryWithRedirect(baseURL, false)(
					{ url: targetUrl, method: "POST", formData: true, body },
					api,
					extraOptions
				);
				if (result.error) return { error: result.error };
				return { data: (result.data as CreateSupportCaseResponse)?.data ?? {} };
			}
		}),

		getSupportOrders: builder.query<GetSupportOrdersData, GetSupportOrdersArgs>({
			query: ({ accountId, search }) => endpoints.support.getOrders(accountId, search),
			transformResponse: (response: GetSupportOrdersResponse) => response?.data
		}),

		getSupportContacts: builder.query<GetSupportContactsData, void>({
			query: () => endpoints.support.getContacts(),
			transformResponse: (response: GetSupportContactsResponse) => response?.data
		}),

		getSupportCases: builder.query<GetSupportCasesData, GetSupportCasesArgs | void>({
			query: (request) => endpoints.support.getCases(request?.search),
			transformResponse: (response: GetSupportCasesResponse) => response?.data,
			providesTags: ["SupportCases"]
		}),

		escalateSupportCase: builder.mutation<EscalateSupportCaseData, EscalateSupportCaseArgs>({
			query: ({ caseId, request }) => ({
				url: endpoints.support.escalateCase(caseId),
				method: "POST",
				dataType: "json",
				headers: {
					"Content-type": "application/json; charset=UTF-8"
				},
				body: request
			}),
			transformResponse: (response: EscalateSupportCaseResponse) => response?.data,
			invalidatesTags: ["SupportCases"]
		}),

		closeSupportCase: builder.mutation<CloseSupportCaseData, CloseSupportCaseArgs>({
			query: ({ caseId }) => ({
				url: endpoints.support.closeCase(caseId),
				method: "POST",
				dataType: "json"
			}),
			transformResponse: (response: CloseSupportCaseResponse) => response?.data,
			invalidatesTags: ["SupportCases"]
		})
	})
});

export const {
	useCreateSupportCaseMutation,
	useGetSupportOrdersQuery,
	useGetSupportContactsQuery,
	useGetSupportCasesQuery,
	useEscalateSupportCaseMutation,
	useCloseSupportCaseMutation
} = supportApiSlice;
